import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Workflow,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { useWorkflows, useCancelWorkflow } from '@/hooks';
import { DataTable } from '@/components/DataTable';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  EmptyState,
  SkeletonTable,
  Input,
} from '@/components/ui';
import {
  formatRelativeTime,
  formatDuration,
  capitalize,
} from '@/lib/utils';
import type { Workflow as WorkflowType, WorkflowStatus } from '@/types';

const columnHelper = createColumnHelper<WorkflowType>();

export function WorkflowsList() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const { data, isLoading, error, refetch, isFetching } = useWorkflows({
    status: statusFilter || undefined,
  });
  const cancelWorkflow = useCancelWorkflow();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Workflow',
        cell: (info) => (
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <Workflow className="h-4 w-4 text-accent" />
            </div>
            <div>
              <p className="font-medium text-foreground">{info.getValue()}</p>
              <Link
                to="/projects/$id"
                params={{ id: info.row.original.projectId }}
                className="text-xs text-accent hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                View Project
              </Link>
            </div>
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue();
          return <WorkflowStatusBadge status={status} />;
        },
      }),
      columnHelper.accessor('trigger', {
        header: 'Trigger',
        cell: (info) => (
          <span className="text-foreground-muted capitalize">
            {info.getValue().replace('_', ' ')}
          </span>
        ),
      }),
      columnHelper.accessor('startedAt', {
        header: 'Started',
        cell: (info) => (
          <span className="text-foreground-muted">
            {formatRelativeTime(info.getValue())}
          </span>
        ),
      }),
      columnHelper.accessor('duration', {
        header: 'Duration',
        cell: (info) => {
          const duration = info.getValue();
          return (
            <span className="text-foreground-muted">
              {duration !== null ? formatDuration(duration) : '-'}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (info) => {
          const workflow = info.row.original;
          if (workflow.status === 'running' || workflow.status === 'pending') {
            return (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelWorkflow.mutate(workflow.id);
                }}
                disabled={cancelWorkflow.isPending}
              >
                Cancel
              </Button>
            );
          }
          return null;
        },
      }),
    ],
    [cancelWorkflow]
  );

  const workflows = data?.data ?? [];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'running', label: 'Running' },
    { value: 'pending', label: 'Pending' },
    { value: 'success', label: 'Success' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  if (error) {
    return (
      <Card variant="bordered">
        <CardContent className="py-12">
          <EmptyState
            icon={<Workflow className="h-12 w-12" />}
            title="Failed to load workflows"
            description="An error occurred while loading the workflows. Please try again."
            action={
              <Button variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Workflows</h1>
          <p className="text-foreground-muted mt-1">
            Monitor and manage workflow runs
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card variant="bordered">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-foreground-muted">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filter by:</span>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 rounded-lg border border-border bg-background-tertiary px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Workflows table */}
      <Card variant="bordered">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Workflows</CardTitle>
            {data?.meta && (
              <span className="text-sm text-foreground-muted">
                {data.meta.totalCount} total
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={10} />
          ) : workflows.length === 0 ? (
            <EmptyState
              icon={<Workflow className="h-12 w-12" />}
              title="No workflows found"
              description={
                statusFilter
                  ? `No workflows with status "${statusFilter}" found.`
                  : 'No workflows have been run yet.'
              }
              action={
                statusFilter ? (
                  <Button variant="secondary" onClick={() => setStatusFilter('')}>
                    Clear Filter
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <DataTable columns={columns} data={workflows} pageSize={10} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function WorkflowStatusBadge({ status }: { status: WorkflowStatus }) {
  const config: Record<
    WorkflowStatus,
    { variant: 'success' | 'error' | 'warning' | 'info' | 'default'; icon: React.ReactNode }
  > = {
    success: {
      variant: 'success',
      icon: <CheckCircle className="h-3 w-3" />,
    },
    failed: {
      variant: 'error',
      icon: <XCircle className="h-3 w-3" />,
    },
    running: {
      variant: 'info',
      icon: <Loader2 className="h-3 w-3 animate-spin" />,
    },
    pending: {
      variant: 'default',
      icon: <Clock className="h-3 w-3" />,
    },
    cancelled: {
      variant: 'warning',
      icon: <XCircle className="h-3 w-3" />,
    },
  };

  const { variant, icon } = config[status];

  return (
    <Badge variant={variant} className="gap-1">
      {icon}
      {capitalize(status)}
    </Badge>
  );
}
